// src/features/auth/components/TwoFactorSetupWizard.tsx
import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { authService } from '../services/authService';
import { TwoFactorEnableRequest } from '../types';
import QRCodeDisplay from './QRCodeDisplay';
import ManualEntryOption from './ManualEntryOption';
import AppRecommendations from './AppRecommendations';
import VerificationForm from './VerificationForm';
import BackupCodesModal from './BackupCodesModal';
import ProgressIndicator from './ProgressIndicator';

interface TwoFactorSetupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

type SetupStep = 'app-selection' | 'qr-code' | 'verification' | 'backup-codes' | 'complete';

interface SetupData {
  qr_code?: string;
  secret_key?: string;
  backup_codes?: string[];
}

const TwoFactorSetupWizard: React.FC<TwoFactorSetupWizardProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const [currentStep, setCurrentStep] = useState<SetupStep>('app-selection');
  const [setupData, setSetupData] = useState<SetupData>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);

  // Rate limit countdown timer
  useEffect(() => {
    if (rateLimitCountdown > 0) {
      const timer = setTimeout(() => {
        setRateLimitCountdown(rateLimitCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [rateLimitCountdown]);

  const steps: { key: SetupStep; title: string; description: string }[] = [
    { key: 'app-selection', title: 'Choose App', description: 'Select authenticator app' },
    { key: 'qr-code', title: 'Scan Code', description: 'Add to your app' },
    { key: 'verification', title: 'Verify', description: 'Enter verification code' },
    { key: 'backup-codes', title: 'Backup Codes', description: 'Save recovery codes' },
    { key: 'complete', title: 'Complete', description: '2FA enabled' }
  ];

  const initializeSetup = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authService.setup2FA();
      
      setSetupData({
        qr_code: response.qr_code_image,
        secret_key: response.secret_key,
        backup_codes: response.backup_codes || []
      });
      setCurrentStep('qr-code');
    } catch (error: any) {
      
      // For development/testing purposes, provide mock data if API fails
      if (process.env.NODE_ENV === 'development') {
        const mockQRCode = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
        const mockSecretKey = 'JBSWY3DPEHPK3PXP';
        const mockBackupCodes = [
          '12345678', '87654321', '11111111', '22222222',
          '33333333', '44444444', '55555555', '66666666'
        ];
        
        setSetupData({
          qr_code: mockQRCode,
          secret_key: mockSecretKey,
          backup_codes: mockBackupCodes
        });
        setCurrentStep('qr-code');
        return;
      }
      
      if (error.detail?.includes('already enabled')) {
        setError('Two-factor authentication is already enabled for your account.');
        setTimeout(() => {
          onClose();
          // Could redirect to settings here
        }, 2000);
      } else {
        setError('Failed to initialize 2FA setup. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (code: string) => {
    try {
      setLoading(true);
      setError(null);
      
      
      const response = await authService.enable2FA({ totp_code: code });
      
      
      // First check for is_enabled flag (most reliable indicator)
      if (response.is_enabled === true) {
        
        // Check if we have backup codes in the response
        if (response.backup_codes && response.backup_codes.length > 0) {
          setSetupData(prev => ({
            ...prev,
            backup_codes: response.backup_codes
          }));
          setCurrentStep('backup-codes');
        } else {
          // If no backup codes in the response, check if we already have them from initial setup
          
          if (setupData.backup_codes && setupData.backup_codes.length > 0) {
            // We already have backup codes from the initial setup
            setCurrentStep('backup-codes');
          } else {
            // If still no backup codes, try to fetch them
            try {
              const statusResponse = await authService.get2FAStatus();
              
              if (statusResponse.backup_codes && statusResponse.backup_codes.length > 0) {
                setSetupData(prev => ({
                  ...prev,
                  backup_codes: statusResponse.backup_codes
                }));
                setCurrentStep('backup-codes');
              } else {
                // As a last resort, try to get backup codes from the initial setup endpoint
                try {
                  const setupResponse = await authService.setup2FA();
                  
                  if (setupResponse.backup_codes && setupResponse.backup_codes.length > 0) {
                    setSetupData(prev => ({
                      ...prev,
                      backup_codes: setupResponse.backup_codes
                    }));
                    setCurrentStep('backup-codes');
                  } else {
                    // No backup codes available, go to completion
                    setCurrentStep('complete');
                  }
                } catch (setupError) {
                  // No backup codes available, go to completion
                  setCurrentStep('complete');
                }
              }
            } catch (statusError) {
              // Even if status fetch fails, 2FA is still enabled
              setCurrentStep('complete');
            }
          }
        }
      } 
      // Fallback to message check if is_enabled is not present
      else if (response.message && (response.message.includes('successfully') || response.message.includes('enabled'))) {
        
        if (response.backup_codes && response.backup_codes.length > 0) {
          setSetupData(prev => ({
            ...prev,
            backup_codes: response.backup_codes
          }));
          setCurrentStep('backup-codes');
        } else {
          // If no backup codes in the response, check if we already have them from initial setup
          
          if (setupData.backup_codes && setupData.backup_codes.length > 0) {
            // We already have backup codes from the initial setup
            setCurrentStep('backup-codes');
          } else {
            // If still no backup codes, try to fetch them
            try {
              const statusResponse = await authService.get2FAStatus();
              
              if (statusResponse.backup_codes && statusResponse.backup_codes.length > 0) {
                setSetupData(prev => ({
                  ...prev,
                  backup_codes: statusResponse.backup_codes
                }));
                setCurrentStep('backup-codes');
              } else {
                // No backup codes available, go to completion
                setCurrentStep('complete');
              }
            } catch (statusError) {
              // Even if status fetch fails, 2FA is still enabled
              setCurrentStep('complete');
            }
          }
        }
      } 
      // Last fallback: if we have backup codes, assume success
      else if (response.backup_codes && response.backup_codes.length > 0) {
        setSetupData(prev => ({
          ...prev,
          backup_codes: response.backup_codes
        }));
        setCurrentStep('backup-codes');
      } 
      // No indication of success
      else {
        setError('Verification failed. Please try again.');
      }
    } catch (error: any) {
      
      // Handle specific error cases
      if (error.detail) {
        if (error.detail.includes('Invalid token') || error.detail.includes('invalid')) {
          setError('Invalid verification code. Please check your authenticator app and try again.');
        } else if (error.detail.includes('rate limit') || error.detail.includes('too many')) {
          setError('Too many attempts. Please wait before trying again.');
          setRateLimitCountdown(60);
        } else if (error.detail.includes('already enabled')) {
          setError('Two-factor authentication is already enabled for your account.');
          setTimeout(() => {
            onClose();
          }, 2000);
        } else {
          setError(error.detail);
        }
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('Verification failed. Please try again.');
      }
      
      setRetryCount(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCodesAcknowledged = () => {
    setCurrentStep('complete');
    setTimeout(() => {
      onComplete();
    }, 1500);
  };

  const handleRetry = () => {
    setError(null);
    setRetryCount(0);
    setRateLimitCountdown(0);
    initializeSetup();
  };

  const handleStepNavigation = (stepKey: string) => {
    const step = stepKey as SetupStep;
    
    // Allow navigation to any step for now, but in a real app you might want to:
    // - Allow forward navigation only if previous steps are completed
    // - Allow backward navigation to any completed step
    // For the 2FA setup flow, we'll allow moving forward when the user clicks continue
    
    if (step === 'qr-code' && !setupData.qr_code) {
      // If trying to go to QR code step but no setup data, initialize first
      initializeSetup();
    } else {
      setCurrentStep(step);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-neutral-500 bg-opacity-75 transition-opacity" />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-theme-bg dark:bg-dark-theme-bg rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
                Enable Two-Factor Authentication
              </h3>
              <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary mt-1">
                Secure your account with an additional layer of protection
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Progress Indicator */}
          <ProgressIndicator
            steps={steps}
            currentStep={currentStep}
            onStepClick={handleStepNavigation}
          />

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="ml-3">
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                  {rateLimitCountdown > 0 && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      Retry in {rateLimitCountdown} seconds
                    </p>
                  )}
                  {error.includes('network') && (
                    <button
                      onClick={handleRetry}
                      className="text-xs text-red-600 dark:text-red-400 underline mt-1 hover:no-underline"
                    >
                      Retry
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step Content */}
          <div className="space-y-6">
            {currentStep === 'app-selection' && (
              <AppRecommendations
                onContinue={() => handleStepNavigation('qr-code')}
                loading={loading}
              />
            )}

            {currentStep === 'qr-code' && setupData.qr_code && (
              <div className="space-y-6">
                <QRCodeDisplay
                  qrCode={setupData.qr_code}
                  secretKey={setupData.secret_key}
                />
                <ManualEntryOption secretKey={setupData.secret_key} />
                <div className="flex justify-end">
                  <button
                    onClick={() => setCurrentStep('verification')}
                    className="px-4 py-2 bg-gradient-button text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark transition-opacity duration-150"
                  >
                    I've Added the Account
                  </button>
                </div>
              </div>
            )}

            {currentStep === 'verification' && (
              <VerificationForm
                onSubmit={handleVerificationSubmit}
                loading={loading}
                retryCount={retryCount}
                rateLimitCountdown={rateLimitCountdown}
              />
            )}

            {currentStep === 'backup-codes' && setupData.backup_codes && (
              <BackupCodesModal
                backupCodes={setupData.backup_codes}
                onAcknowledged={handleBackupCodesAcknowledged}
              />
            )}

            {currentStep === 'complete' && (
              <div className="text-center py-8">
                <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-theme-text dark:text-dark-theme-text mb-2">
                  Two-Factor Authentication Enabled!
                </h4>
                <p className="text-theme-text-secondary dark:text-dark-theme-text-secondary">
                  Your account is now protected with 2FA
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorSetupWizard;
